<template>
  <div class="audio-waveform-container" v-if="isVisible">
    <canvas 
      ref="waveformCanvas" 
      :width="canvasWidth" 
      :height="canvasHeight"
      class="waveform-canvas"
    ></canvas>
  </div>
</template>

<script>
export default {
  name: 'AudioWaveform',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    canvasWidth: {
      type: Number,
      default: 300
    },
    canvasHeight: {
      type: Number,
      default: 100
    }
  },
  data() {
    return {
      animationId: null,
      audioData: new Array(64).fill(0), // 存储64个采样点
      volumeLevel: 0,
      smoothedVolume: 0
    };
  },
  mounted() {
    this.startAnimation();
  },
  beforeDestroy() {
    this.stopAnimation();
  },
  methods: {
    // 更新音频数据
    updateAudioData(inputData) {
      if (!inputData || !this.isVisible) return;
      
      // 计算音量级别 (RMS)
      let sum = 0;
      for (let i = 0; i < inputData.length; i++) {
        sum += inputData[i] * inputData[i];
      }
      this.volumeLevel = Math.sqrt(sum / inputData.length);
      
      // 平滑音量变化
      this.smoothedVolume = this.smoothedVolume * 0.8 + this.volumeLevel * 0.2;
      
      // 对音频数据进行降采样，获取64个采样点用于波形显示
      const samplesPerSegment = Math.floor(inputData.length / this.audioData.length);
      for (let i = 0; i < this.audioData.length; i++) {
        let segmentSum = 0;
        const startIndex = i * samplesPerSegment;
        const endIndex = Math.min(startIndex + samplesPerSegment, inputData.length);
        
        for (let j = startIndex; j < endIndex; j++) {
          segmentSum += Math.abs(inputData[j]);
        }
        
        // 取平均值并进行平滑处理
        const newValue = segmentSum / (endIndex - startIndex);
        this.audioData[i] = this.audioData[i] * 0.7 + newValue * 0.3;
      }
    },
    
    // 开始动画
    startAnimation() {
      this.animate();
    },
    
    // 停止动画
    stopAnimation() {
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
        this.animationId = null;
      }
    },
    
    // 动画循环
    animate() {
      this.drawWaveform();
      this.animationId = requestAnimationFrame(this.animate);
    },
    
    // 绘制波形
    drawWaveform() {
      const canvas = this.$refs.waveformCanvas;
      if (!canvas) return;
      
      const ctx = canvas.getContext('2d');
      const width = canvas.width;
      const height = canvas.height;
      
      // 清空画布
      ctx.clearRect(0, 0, width, height);
      
      // 设置背景
      ctx.fillStyle = 'rgba(0, 20, 40, 0.8)';
      ctx.fillRect(0, 0, width, height);
      
      // 绘制网格线
      this.drawGrid(ctx, width, height);
      
      // 绘制波形
      this.drawWaveformBars(ctx, width, height);
    },
    
    // 绘制网格线
    drawGrid(ctx, width, height) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
      ctx.lineWidth = 1;
      
      // 水平网格线
      for (let i = 0; i <= 4; i++) {
        const y = (height / 4) * i;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }
      
      // 垂直网格线
      const barWidth = width / this.audioData.length;
      for (let i = 0; i <= this.audioData.length; i += 8) {
        const x = barWidth * i;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }
    },
    
    // 绘制波形柱状图
    drawWaveformBars(ctx, width, height) {
      const barWidth = (width / this.audioData.length) * 0.8;
      const spacing = (width / this.audioData.length) * 0.2;
      
      for (let i = 0; i < this.audioData.length; i++) {
        const barHeight = this.audioData[i] * height * 2; // 放大显示
        const x = i * (barWidth + spacing) + spacing / 2;
        const y = (height - barHeight) / 2;
        
        // 创建渐变色
        const gradient = ctx.createLinearGradient(0, y, 0, y + barHeight);
        
        // 根据音量级别调整颜色
        if (this.audioData[i] > 0.5) {
          gradient.addColorStop(0, '#ff4757'); // 红色 (高音量)
          gradient.addColorStop(1, '#ff6b7a');
        } else if (this.audioData[i] > 0.2) {
          gradient.addColorStop(0, '#ffa726'); // 橙色 (中音量)
          gradient.addColorStop(1, '#ffb74d');
        } else {
          gradient.addColorStop(0, '#26a69a'); // 绿色 (低音量)
          gradient.addColorStop(1, '#4db6ac');
        }
        
        ctx.fillStyle = gradient;
        ctx.fillRect(x, y, barWidth, barHeight);
        
        // 添加高光效果
        if (this.audioData[i] > 0.1) {
          ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
          ctx.fillRect(x, y, barWidth, Math.max(barHeight * 0.3, 2));
        }
      }
    },
    
    // 重置波形数据
    reset() {
      this.audioData = new Array(64).fill(0);
      this.volumeLevel = 0;
      this.smoothedVolume = 0;
    }
  }
};
</script>

<style lang="scss" scoped>
.audio-waveform-container {
  position: absolute;
  top: -40px;
  left: 50%;
  transform: translateX(280px);
  .waveform-title {
    color: #666;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 8px;
    text-align: center;
    // text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  }
  
  .waveform-canvas {
    display: block;
    margin: 0 auto;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(0, 0, 0, 0.2);
  }
  
  .volume-indicator {
    display: flex;
    align-items: center;
    margin-top: 10px;
    gap: 10px;
    
    .volume-bar {
      flex: 1;
      height: 6px;
      background: rgba(255, 255, 255, 0.2);
      border-radius: 3px;
      overflow: hidden;
      
      .volume-fill {
        height: 100%;
        background: linear-gradient(90deg, #26a69a 0%, #ffa726 50%, #ff4757 100%);
        border-radius: 3px;
        transition: width 0.1s ease-out;
      }
    }
    
    .volume-text {
      color: white;
      font-size: 12px;
      font-weight: 500;
      min-width: 35px;
      text-align: right;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
    }
  }
}

// 响应式设计
@media (max-width: 600px) {
  .audio-waveform-container {
    padding: 12px;
    margin: 8px 0;
    
    .waveform-title {
      font-size: 12px;
    }
    
    .volume-text {
      font-size: 11px;
      min-width: 30px;
    }
  }
}
</style>
